import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import cn from 'classnames';

import { arrowRenderer, clearRenderer } from '@lightspeed/cirrus-autocomplete';
import Dropdown from '@lightspeed/cirrus-select';
import Group, { GroupAddon } from '@lightspeed/cirrus-group';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { IconClose } from '@lightspeed/flame/Icon/Close';
import Input from '@lightspeed/cirrus-input';
import { ModalBody } from '@lightspeed/cirrus-modal';
import { Spinner } from '@lightspeed/flame/Spinner';
import Text from '@lightspeed/cirrus-text';
import { colors } from '@lightspeed/cirrus-tokens';

import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import Decimal from 'app/src/data/base/Decimal';
import Localize from 'app/src/localize/Localize';

import { PaymentMethod, TransactionState, CTS_CONTAINER_ID } from './ManualPaymentTypes';
import ManualPaymentBodySupplemental from './ManualPaymentBodySupplemental';
import ManualPaymentErrorDetails from './ManualPaymentErrorDetails';

class ManualPaymentBody extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedTerminal: props.paymentProvider.getTerminal(
        CommonData.selectedRegister.get('ccTerminalID')
      ),
      selectedManualMethod: PaymentMethod.MANUAL_TERMINAL,
      postalCode: props.postalCode || '',
      cardHolderName: props.cardHolderName || '',
      amount: props.balance,
      balance: this.getOutstandingBalance(props.amount),
    };

    this.handleManualMethodSelected = this.handleManualMethodSelected.bind(this);
    this.handleTerminalSelected = this.handleTerminalSelected.bind(this);
    this.handlePostalCodeChange = this.handlePostalCodeChange.bind(this);
    this.handleCardHolderNameChange = this.handleCardHolderNameChange.bind(this);
    this.handleAmountChange = this.handleAmountChange.bind(this);
    this.handleAmountBlur = this.handleAmountBlur.bind(this);
  }

  componentDidMount() {
    const {
      activeGatewaySupportsManualEntry,
      activeGatewaySupportsManualEntryFromTerminal,
      onManualMethodSelectedChange,
    } = this.props;

    // We favor the POS manual entry first for Stripe Merchants
    if (activeGatewaySupportsManualEntry) {
      this.setState({ selectedManualMethod: PaymentMethod.MANUAL_POS }, () => {
        onManualMethodSelectedChange(PaymentMethod.MANUAL_POS);
      });
    } else if (activeGatewaySupportsManualEntryFromTerminal) {
      // If manual entry on terminal is available we will fallback to it for WP Merchants
      this.setState({ selectedManualMethod: PaymentMethod.MANUAL_TERMINAL }, () => {
        onManualMethodSelectedChange(PaymentMethod.MANUAL_TERMINAL);
      });
    } else {
      this.setState({ selectedManualMethod: PaymentMethod.MANUAL_TERMINAL }, () => {
        onManualMethodSelectedChange(PaymentMethod.MANUAL_TERMINAL);
      });
    }
  }

  // eslint-disable-next-line react/sort-comp
  handleAmountChange(event) {
    const amount = event.target.value;
    const { onAmountChange } = this.props;

    this.setState(
      () => ({ amount }),
      () => {
        onAmountChange(amount);
      }
    );
  }

  handleAmountBlur(event) {
    const { balance, onAmountChange } = this.props;

    let amount = Format.parseDecimal(Format.formatMoney(event.target.value, 0, false), 0);
    amount = amount.isGreaterThan(balance) ? Format.parseDecimal(balance) : amount;

    this.setState(
      () => ({
        amount: Format.formatMoney(amount, 0, false),
        balance: this.getOutstandingBalance(amount),
      }),
      () => {
        onAmountChange(amount);
      }
    );
  }

  handleManualMethodSelected(event) {
    const manualMethod = event.target.value;
    const { onManualMethodSelectedChange } = this.props;

    this.setState({ selectedManualMethod: manualMethod }, () => {
      onManualMethodSelectedChange(manualMethod);
    });
  }

  handleTerminalSelected(terminalID) {
    const { paymentProvider, onTerminalSelectedChange } = this.props;
    const selectedTerminal = paymentProvider.getTerminal(terminalID);

    CommonData.selectedRegister.set('ccTerminalID', terminalID).save();

    this.setState(
      () => ({
        selectedTerminal,
      }),
      () => {
        onTerminalSelectedChange(selectedTerminal);
      }
    );
  }

  handlePostalCodeChange(event) {
    const { onPostalCodeChange } = this.props;
    const postalCode = event.target.value;

    this.setState(
      {
        postalCode: event.target.value,
      },
      () => {
        onPostalCodeChange(postalCode);
      }
    );
  }

  handleCardHolderNameChange(event) {
    const { onCardHolderNameChange } = this.props;
    const cardHolderName = event.target.value;

    this.setState(
      {
        cardHolderName: event.target.value,
      },
      () => {
        onCardHolderNameChange(cardHolderName);
      }
    );
  }

  getOutstandingBalance(amount) {
    return this.props.balance.minus(amount);
  }

  render() {
    const {
      hasMultipleManualPaymentMethod,
      paymentProvider,
      transactionState,
      error,
      payment,
    } = this.props;

    const {
      amount,
      balance,
      selectedTerminal,
      selectedManualMethod,
      postalCode,
      cardHolderName,
    } = this.state;
    const selectedTerminalId = selectedTerminal ? selectedTerminal.model.id : '';

    let bodyMain = null;

    if (transactionState === TransactionState.WAITING_TO_START) {
      const terminalDropdownData = paymentProvider.terminalCollection.map(terminal => ({
        value: terminal.id,
        label: terminal.get('name'),
      }));

      bodyMain = (
        <div className="cirrus quick-pay-modal__body">
          <div className="">
            <label>Amount</label>
            <Group noSpacing inputBlock>
              <GroupAddon>{Localize.getCurrencySymbol()}</GroupAddon>
              <Input
                value={amount}
                onChange={this.handleAmountChange}
                onBlur={this.handleAmountBlur}
              />
            </Group>
          </div>
          <div />
          <div className="cr-mt-2">
            {hasMultipleManualPaymentMethod && (
              <Fragment>
                <label>Make a Manual Payment From:</label>
                <Dropdown
                  id="manual-pay-method"
                  name="manualPaymentMethod"
                  placeholder="Select a Payment Source"
                  value={selectedManualMethod}
                  onChange={this.handleManualMethodSelected}
                >
                  <option value={PaymentMethod.MANUAL_TERMINAL}>Terminal</option>
                  <option value={PaymentMethod.MANUAL_POS}>Retail</option>
                </Dropdown>
              </Fragment>
            )}
            <div className="cr-mt-2">
              {selectedManualMethod === PaymentMethod.MANUAL_TERMINAL && (
                <Fragment>
                  <label>Terminal</label>
                  <Select
                    name="terminal"
                    placeholder="Select a Terminal"
                    value={selectedTerminalId}
                    options={terminalDropdownData}
                    inputProps={{ id: 'manual-pay-terminal-select' }}
                    escapeClearsValue={false}
                    autosize={false}
                    simpleValue
                    matchProp="label"
                    onChange={this.handleTerminalSelected}
                    arrowRenderer={arrowRenderer}
                    clearRenderer={clearRenderer}
                    autoBlur
                  />
                </Fragment>
              )}
              {selectedManualMethod === PaymentMethod.MANUAL_POS && (
                <Group type="vertical">
                  <div>
                    <label>Name on Card</label>
                    <Input
                      id="cardHolderName"
                      value={cardHolderName}
                      onChange={this.handleCardHolderNameChange}
                      autoFocus
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label>Postal Code/Zip</label>
                    <Input
                      id="manualPaymentPostalCode"
                      value={postalCode}
                      onChange={this.handlePostalCodeChange}
                      autoFocus
                      autoComplete="off"
                    />
                  </div>
                </Group>
              )}
            </div>
          </div>
          <ManualPaymentBodySupplemental
            transactionState={transactionState}
            payment={payment}
            amount={amount}
            balance={balance}
          />
        </div>
      );
    } else if (transactionState === TransactionState.ENTER_CARD_DETAILS) {
      const { isManualAuthorized } = this.props;
      const ctsClassName = cn('cr-flex', 'cr-justify-center', {
        hidden: !isManualAuthorized,
      });

      bodyMain = (
        <div className="cirrus quick-pay-modal__body">
          <div id={CTS_CONTAINER_ID} className={ctsClassName} />

          {!isManualAuthorized && (
            <Fragment>
              <div className="quick-pay-modal__body__icon">
                <Spinner color="blue-300" baseColor2="gray-200" size="96px" />
              </div>
              <div className="quick-pay-modal__body__message">
                <Text bodySmall>Contacting servers...</Text>
              </div>
            </Fragment>
          )}
        </div>
      );
    } else {
      let image = null;
      let message = null;
      const { colorsMap } = colors;

      switch (transactionState) {
        case TransactionState.IN_PROGRESS:
          image = <Spinner color="blue-300" baseColor2="gray-200" size="96px" />;
          message =
            selectedManualMethod === PaymentMethod.MANUAL_POS
              ? 'Processing Transaction'
              : 'Complete Payment on Terminal';
          break;

        case TransactionState.COMPLETE:
          // eslint-disable-next-line
          const { balance, payment } = this.props;
          const isPartiallyApproved = !payment.amount.isEqualTo(amount);
          const iconColor = isPartiallyApproved ? colorsMap['orange-200'] : colorsMap['green-200'];
          image = <IconVerified size="96px" color={iconColor} />;
          message = isPartiallyApproved ? 'Transaction Partially Approved' : 'Payment Approved';
          break;

        case TransactionState.CANCELED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          message = 'Payment Canceled by Customer';
          break;

        case TransactionState.DECLINED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          message = 'Payment Declined';
          break;

        case TransactionState.FAILED:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          message = 'Payment Failed';
          break;

        case TransactionState.ERROR:
          image = <IconClose size="96px" color={colorsMap.maple} />;
          message = 'Payment Error';
          break;

        case TransactionState.CANCEL_FROM_TERMINAL:
          image = (
            <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/payments/cancelFromGeniusTerminal@2x.png" title="terminal" alt="" />
          );
          break;

        default:
          break;
      }

      bodyMain = (
        <div className="quick-pay-modal__body cirrus">
          <div className="quick-pay-modal__body__icon">{image}</div>
          <div className="quick-pay-modal__body__message">
            {message && <Text bodySmall>{message}</Text>}
            {error && <ManualPaymentErrorDetails error={error} />}
          </div>
          <ManualPaymentBodySupplemental
            transactionState={transactionState}
            payment={payment}
            amount={amount}
            balance={balance}
          />
        </div>
      );
    }

    return <ModalBody>{bodyMain}</ModalBody>;
  }
}

ManualPaymentBody.propTypes = {
  amount: PropTypes.instanceOf(Decimal).isRequired,
  balance: PropTypes.instanceOf(Decimal).isRequired,
  cardHolderName: PropTypes.string,
  error: PropTypes.string,
  hasMultipleManualPaymentMethod: PropTypes.bool,
  isManualAuthorized: PropTypes.bool,
  onAmountChange: PropTypes.func,
  onCardHolderNameChange: PropTypes.func,
  onManualMethodSelectedChange: PropTypes.func,
  onPostalCodeChange: PropTypes.func,
  onTerminalSelectedChange: PropTypes.func,
  payment: PropTypes.shape(PropTypes.any.object),
  paymentProvider: PropTypes.shape({
    getTerminal: PropTypes.func,
    terminalCollection: PropTypes.shape(PropTypes.any.object),
  }),
  postalCode: PropTypes.string,
  activeGatewaySupportsManualEntry: PropTypes.bool,
  activeGatewaySupportsManualEntryFromTerminal: PropTypes.bool,
  transactionState: PropTypes.string,
};

ManualPaymentBody.defaultProps = {
  postalCode: null,
  cardHolderName: null,
  error: null,
  payment: null,
  transactionState: TransactionState.WAITING_TO_START,
  onAmountChange: () => {},
  onManualMethodSelectedChange: () => {},
  onTerminalSelectedChange: () => {},
  onPostalCodeChange: () => {},
  onCardHolderNameChange: () => {},
  isManualAuthorized: () => {},
};

export default ManualPaymentBody;
